"use client";

import { useRef, useState } from "react";
import { DARK_BAND, GradText, SectionHead, SectionShell, useBandRowAlign, useRefGridMode, useWholeRowBlocks } from "./kit";
import { EASE_OUT, gsap, NOT_REDUCED, playOnEnter, useEnterStagger, useGSAP } from "./kit/motion";

/** Per-range bar heights so the 7D/30D toggle actually re-shapes the chart. */
const BARS = [
  { day: "Mon", h: { "7D": 42, "30D": 58 }, alt: false },
  { day: "Tue", h: { "7D": 55, "30D": 47 }, alt: false },
  { day: "Wed", h: { "7D": 38, "30D": 66 }, alt: true },
  { day: "Thu", h: { "7D": 61, "30D": 52 }, alt: false },
  { day: "Fri", h: { "7D": 72, "30D": 78 }, alt: false },
  { day: "Sat", h: { "7D": 49, "30D": 40 }, alt: true },
  { day: "Sun", h: { "7D": 80, "30D": 71 }, alt: false },
];

const DEVICES = [
  { label: "Mobile", value: 44, color: "#FA437F" },
  { label: "Desktop", value: 33, color: "#2F6BE0" },
  { label: "Tablet", value: 23, color: "#12A06E" },
];

const GEOS = [
  { name: "Germany", w: 92, value: "38%" },
  { name: "United States", w: 68, value: "27%" },
  { name: "United Kingdom", w: 44, value: "18%" },
  { name: "France", w: 28, value: "11%" },
  { name: "Other", w: 16, value: "6%" },
];

const RANGES = ["7D", "30D"] as const;
type Range = (typeof RANGES)[number];

function DashCard({
  title,
  control,
  className = "",
  children,
}: {
  title: string;
  control?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-dash-card
      className={`flex h-full flex-col rounded-ref-lg border border-line bg-surface p-[22px] shadow-ref ${className}`}
    >
      <div className="mb-[18px] flex shrink-0 items-center justify-between">
        <h4 className="text-[15px] font-semibold">{title}</h4>
        {control}
      </div>
      {children}
    </div>
  );
}

const DONUT_C = 2 * Math.PI * 56;

/** SVG donut whose segments sweep in on scroll (see the section's useGSAP). */
function Donut() {
  let offset = 0;
  return (
    <svg viewBox="0 0 128 128" className="h-32 w-32 shrink-0 -rotate-90">
      <circle cx="64" cy="64" r="56" fill="none" stroke="var(--color-surface-3)" strokeWidth="16" />
      {DEVICES.map((d) => {
        const len = (d.value / 100) * DONUT_C;
        const el = (
          <circle
            key={d.label}
            data-donut-seg
            cx="64"
            cy="64"
            r="56"
            fill="none"
            stroke={d.color}
            strokeWidth="16"
            strokeDasharray={`${len} ${DONUT_C - len}`}
            strokeDashoffset={-offset}
          />
        );
        offset += len;
        return el;
      })}
    </svg>
  );
}

export default function AnalyticsDashboardSection() {
  const [range, setRange] = useState<Range>("7D");
  // Dark beat in the homepage's light/dark alternation; light elsewhere.
  const onGrid = useRefGridMode();
  const gridRef = useRef<HTMLDivElement>(null);
  const nullRef = useRef<HTMLDivElement>(null); // never attached — disables the hooks off-grid
  const mounted = useRef(false);

  // useWholeRowBlocks/useBandRowAlign pad and re-align [data-grid-block]s for
  // grid-paper layout — running them on /solutions/marketing (non-grid
  // RefSections) would inject inline padding/margins into this SHARED section
  // and break "visually unchanged". Hooks can't be called conditionally, so
  // gate them by ref instead: both bail immediately once `innerRef.current` is
  // null, so pointing them at `nullRef` (never attached) off-grid is a full
  // no-op rather than a skipped hook call. Default breathing row: the grid
  // holds three separate DashCards, not one enclosing opaque card.
  useWholeRowBlocks(onGrid ? gridRef : nullRef);
  useBandRowAlign(onGrid ? gridRef : nullRef);

  // Cards settle in first…
  useEnterStagger(gridRef, "[data-dash-card]", { y: 30, stagger: 0.12 });

  // …then the data draws itself: bars grow, donut segments sweep, geo bars fill.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const tl = gsap
          .timeline({ paused: true, defaults: { ease: EASE_OUT } })
          .from("[data-bar]", { height: 0, duration: 1, stagger: 0.07 }, 0.25)
          .from(
            "[data-donut-seg]",
            {
              strokeDasharray: `0 ${DONUT_C}`,
              strokeDashoffset: 0,
              duration: 0.9,
              stagger: 0.12,
            },
            0.35,
          )
          .from(
            "[data-geo-bar]",
            { scaleX: 0, transformOrigin: "left center", duration: 0.9, stagger: 0.08 },
            0.45,
          );
        return playOnEnter(tl, gridRef.current);
      });
    },
    { scope: gridRef },
  );

  // Range toggle: bars morph to the new distribution.
  useGSAP(
    () => {
      if (!mounted.current) {
        mounted.current = true;
        return;
      }
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        gsap.utils.toArray<HTMLElement>("[data-bar]", gridRef.current ?? undefined).forEach((el, i) => {
          gsap.to(el, {
            height: `${BARS[i].h[range]}%`,
            duration: 0.7,
            ease: EASE_OUT,
            overwrite: "auto",
          });
        });
      });
    },
    { dependencies: [range], scope: gridRef },
  );

  return (
    <SectionShell
      divider
      band="dark"
      decoAvoid
      className={onGrid ? "max-md:bg-[#14141c]" : ""}
    >
      <SectionHead
        eyebrow="Deep Analytics"
        tone={onGrid ? "light" : undefined}
        gridFill={onGrid ? DARK_BAND : undefined}
        title={
          <>
            Every click, <GradText>visualized beautifully.</GradText>
          </>
        }
        lead="Live data on clicks, devices, browsers, referrers and countries — bots filtered out, the clean signal, none of the noise."
      />

      <div
        ref={gridRef}
        data-grid-block
        data-grid-fill={onGrid ? DARK_BAND : undefined}
        className="grid gap-[18px] lg:grid-cols-[1.5fr_1fr]"
      >
        {/* traffic velocity (spans both rows on lg) */}
        <DashCard
          title="Traffic velocity"
          className="lg:row-span-2"
          control={
            <div className="flex gap-[3px] rounded-lg bg-surface-3 p-[3px]">
              {RANGES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${
                    range === r ? "bg-surface text-ink shadow-[0_1px_4px_rgba(0,0,0,0.08)]" : "text-muted"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          }
        >
          <div className="flex min-h-[170px] flex-1 items-end gap-2.5 pt-2.5">
            {BARS.map((bar) => (
              <div key={bar.day} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                <div
                  data-bar
                  className={`w-full max-w-[34px] rounded-t-[7px] rounded-b-[3px] ${
                    bar.alt
                      ? "bg-[linear-gradient(180deg,#d9d9e2,#c2c2cf)]"
                      : "bg-[linear-gradient(180deg,var(--color-brand-500),var(--color-brand-600))]"
                  }`}
                  style={{ height: `${bar.h["7D"]}%` }}
                />
                <div className="text-[11px] text-dim">{bar.day}</div>
              </div>
            ))}
          </div>
        </DashCard>

        {/* device distribution */}
        <DashCard title="Device distribution">
          <div className="flex items-center gap-[22px]">
            <Donut />
            <div className="flex flex-1 flex-col gap-3">
              {DEVICES.map((d) => (
                <div key={d.label} className="flex items-center gap-2.5 text-[13.5px]">
                  <span className="h-[11px] w-[11px] rounded-[3px]" style={{ background: d.color }} />
                  {d.label}
                  <span className="ml-auto font-semibold tabular-nums">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </DashCard>

        {/* countries */}
        <DashCard title="Top countries">
          {GEOS.map((g) => (
            <div key={g.name} className="mb-3.5 flex items-center gap-3 text-[13.5px] last:mb-0">
              <span className="w-[108px] shrink-0 text-muted">{g.name}</span>
              <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-3">
                <span
                  data-geo-bar
                  className="block h-full rounded-full bg-[linear-gradient(90deg,var(--color-brand-800),var(--color-brand-500))]"
                  style={{ width: `${g.w}%` }}
                />
              </span>
              <span className="w-[42px] text-right font-semibold tabular-nums">{g.value}</span>
            </div>
          ))}
        </DashCard>
      </div>
    </SectionShell>
  );
}
